import { SignJWT } from 'jose';

import { Padding } from 'types';

import addHeaderClassesToHtml from './addHeaderClassesToHtml';
import { addBackgroundImage } from './addBackgroundImage';
import { addWatermark } from './addWatermark';

type GetDocumentBlobProps = {
  html: string;
  footerHtml?: string;
  watermarkText?: string;
  isLandscape?: boolean;
  backgroundImage?: string;
  textPadding?: Padding;
};

const fetchCss = async (path: string) => {
  const response = await fetch(path);
  return response.text();
};

const getStyles = async () => {
  const contentCss = await fetchCss('/styles/CKEditor/ck-content.css');
  const editorDefaultCss = await fetchCss('/styles/CKEditor/ckeditor5.css');
  const editorPremiumCss = await fetchCss(
    '/styles/CKEditor/ckeditor5-premium-features.css'
  );
  return `${contentCss}\n${editorDefaultCss}\n${editorPremiumCss}`;
};

export const getDocumentBlob = async ({
  html,
  footerHtml,
  watermarkText,
  isLandscape = false,
  backgroundImage,
  textPadding,
}: GetDocumentBlobProps): Promise<Blob> => {
  const accessKey = process.env.REACT_APP_CKEDITOR_ACCESS_KEY;

  const payload = {
    aud: process.env.REACT_APP_CKEDITOR_ENVIRONMENT_ID,
    iat: Math.floor(Date.now() / 1000),
  };

  const token = await new SignJWT(payload)
    .setProtectedHeader({ alg: 'HS256' })
    .sign(new TextEncoder().encode(accessKey));

  const styles = await getStyles();

  const htmWithHeader = addHeaderClassesToHtml(html);
  const htmlWithBackground = backgroundImage
    ? addBackgroundImage({ html: htmWithHeader, backgroundImage, textPadding })
    : htmWithHeader;

  const resultHtml = addWatermark(htmlWithBackground, watermarkText || '');

  const request = {
    html: resultHtml,
    css: styles,
    options: {
      format: 'A4',
      page_orientation: isLandscape ? 'landscape' : 'portrait',
      footer_html: footerHtml || '',
      ...(backgroundImage
        ? {}
        : {
            margin_top: '20mm',
            margin_bottom: '20mm',
            margin_right: '12mm',
            margin_left: '12mm',
          }),
    },
  };

  const res = await fetch('https://pdf-converter.cke-cs.com/v1/convert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(request),
  });

  const blob = await res.blob();
  return blob;
};
